// "Prepare demo": load Home + every Health page in a hidden same-origin frame so their
// 24h cached copies are built exactly as a real visit would build them.
import api from './api';
import { clearCached, setDemoReady, getDemoReady } from './pageCache';
import { SCRIPT, toSpeech } from '../pages/personal-impact/frontline/tourScript';

export const WARM_PAGES = [
  { path: '/', label: 'Home' },
  { path: '/lei-health', label: 'Learning Health' },
  { path: '/people-engine/health', label: 'Capability Health' },
  { path: '/maturity-health', label: 'Maturity Health' },
  { path: '/roe-health', label: 'Transfer Health' },
  { path: '/performance-health', label: 'Performance Health' },
  { path: '/roi-health', label: 'Financial Health' },
  { path: '/story-guide', label: 'Read the Story' },
];

const MIN_MS = 3000;      // give the page time to mount its widgets
const QUIET_MS = 1500;    // no cacheable fetch finished for this long → settled
const IDLE_GIVEUP_MS = 20000; // lazy route never issued a cacheable call → give up
const TIMEOUT_MS = 60000; // server rebuild after a resync can be slow

export function warmPage(path) {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.name = 'seertech-warmup';
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;width:1280px;height:900px;left:-20000px;top:0;opacity:0;pointer-events:none;';
    const start = Date.now();
    const finish = (ok) => {
      clearInterval(timer);
      const s = frame.contentWindow?.__pageCacheStats;
      const writes = s ? s.writes : 0;
      frame.remove();
      resolve({ path, ok, writes, ms: Date.now() - start });
    };
    const timer = setInterval(() => {
      const s = frame.contentWindow?.__pageCacheStats;
      const age = Date.now() - start;
      if (age > TIMEOUT_MS) return finish(false);
      if (!s || age < MIN_MS) return undefined;
      // Routes are lazy-loaded: wait for the page's first cacheable call before judging it settled.
      const active = s.writes + s.hits + s.inflight > 0;
      if (!active) return age > IDLE_GIVEUP_MS ? finish(false) : undefined;
      if (s.inflight === 0 && Date.now() - s.lastActivity > QUIET_MS) return finish(true);
      return undefined;
    }, 300);
    frame.src = path;
    document.body.appendChild(frame);
  });
}

// Mirror Frontline guided tour: generate (server-cached) narration clips for the presenter's voice and pull them into
// this browser's HTTP cache, so the first narrated tour of the day starts instantly.
export const NARRATION_PAGE = { path: 'tour-narration', label: 'Tour narration' };
export async function warmNarration() {
  const start = Date.now();
  let voice = 'onyx';
  try { voice = localStorage.getItem('mirror_frontline_tour_voice') || 'onyx'; } catch { /* default */ }
  if (voice === 'browser') voice = 'onyx';
  try {
    const { data } = await api.post('/mirror/frontline/narration/prepare', { voice, steps: SCRIPT.map((s) => ({ id: s.id, text: toSpeech(s.text) })) });
    const keys = Object.values(data.clips || {});
    await Promise.all(keys.map((k) => api.get(`/mirror/frontline/narration/clip/${k}.mp3`, { responseType: 'blob' }).catch(() => null)));
    return { ...NARRATION_PAGE, label: `Tour narration (${voice})`, ok: keys.length === SCRIPT.length, ms: Date.now() - start, writes: data.generated || 0 };
  } catch {
    return { ...NARRATION_PAGE, label: `Tour narration (${voice})`, ok: false, ms: Date.now() - start, writes: 0 };
  }
}

// Resync the org's server cache, drop local copies, then rebuild every page in order.
export async function prepareDemo({ organizationId, onProgress } = {}) {
  const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
  const prev = getDemoReady(); // read before the copies (and marker) are wiped
  await api.post(`/health-cache/resync${q}`);
  clearCached();
  const results = [];
  for (const page of WARM_PAGES) {
    onProgress?.({ current: page, done: results });
    results.push(await warmPage(page.path)); // eslint-disable-line no-await-in-loop
  }
  onProgress?.({ current: NARRATION_PAGE, done: results });
  const narration = await warmNarration();
  onProgress?.({ current: null, done: [...results, narration] });
  setDemoReady({
    at: Date.now(), org: organizationId || '',
    views: results.reduce((a, r) => a + r.writes, 0), failed: results.filter((r) => !r.ok).length,
    pages: [...results.map((r) => ({ path: r.path, label: WARM_PAGES.find((w) => w.path === r.path)?.label || r.path, ok: r.ok, ms: r.ms, writes: r.writes })), narration],
    prevPages: prev?.pages || null,
  });
  // Super admins get an in-app alert when a page crossed 25s / timed out (server dedupes per org/page/day).
  api.post('/health-cache/warmup-report', {
    organization_id: organizationId || null,
    total_ms: results.reduce((a, r) => a + r.ms, 0),
    pages: results.map((r) => ({ path: r.path, label: WARM_PAGES.find((w) => w.path === r.path)?.label || r.path, ok: r.ok, ms: r.ms, writes: r.writes })),
  }).catch(() => {});
  return results;
}
